import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload } from "lucide-react";

const PLATFORMS = ["luxy", "gojo", "blackcareverywhere", "b2b", "other"];
const METHODS = ["zelle", "venmo", "cash", "check", "stripe", "other"];

const emptyRow = (prev) => ({
  driver_id: prev?.driver_id || "",
  date: prev?.date || new Date().toISOString().slice(0, 10),
  platform: prev?.platform || "luxy",
  description: "",
  amount: "",
  status: prev?.status || "paid",
  method: prev?.method || "zelle",
});

/** Bulk entry of external/affiliate platform trips (Luxy, GoJo, B2B…). */
export default function ExternalTripsModal({ onClose, onSaved }) {
  const [drivers, setDrivers] = useState([]);
  const [rows, setRows] = useState([emptyRow()]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/drivers").then(r => setDrivers(r.data || [])).catch(() => {});
  }, []);

  const set = (i, k, v) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows(rs => [...rs, emptyRow(rs[rs.length - 1])]);
  const delRow = (i) => setRows(rs => rs.filter((_, j) => j !== i));

  const importCsv = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const lines = String(reader.result).split(/\r?\n/).filter(l => l.trim());
        const header = lines[0].toLowerCase();
        const hasHeader = header.includes("driver") || header.includes("date");
        const body = hasHeader ? lines.slice(1) : lines;
        const byName = {};
        drivers.forEach(d => { byName[(d.name || "").trim().toLowerCase()] = d.driver_id; });
        const parsed = [];
        const unmatched = new Set();
        body.forEach(line => {
          // driver, date, platform, description, amount, status(paid/unpaid), method
          const cols = line.split(",").map(c => c.replace(/^"|"$/g, "").trim());
          if (cols.length < 3) return;
          const [name, date, platform, description = "", amount = "", status = "paid", method = "zelle"] = cols;
          const did = byName[name.toLowerCase()];
          if (!did) { unmatched.add(name); return; }
          parsed.push({ driver_id: did, date, platform: platform || "other", description,
                        amount, status: status.toLowerCase() === "unpaid" ? "unpaid" : "paid", method });
        });
        if (parsed.length) setRows(rs => (rs.length === 1 && !rs[0].amount ? parsed : [...rs, ...parsed]));
        toast.success(`Imported ${parsed.length} row(s)`);
        if (unmatched.size) toast.warning(`Driver name(s) not found: ${[...unmatched].join(", ")} — add them in the Users tab first`);
      } catch (_e) { toast.error("Could not read that CSV"); }
    };
    reader.readAsText(file);
  };

  const save = async () => {
    const clean = rows.filter(r => r.driver_id && r.date && Number(r.amount) > 0);
    if (!clean.length) { toast.error("Fill in at least one row (driver, date, amount)"); return; }
    setBusy(true);
    try {
      const r = await api.post("/payouts/external/bulk", {
        trips: clean.map(x => ({
          driver_id: x.driver_id, date: x.date, platform: x.platform,
          description: x.description || null, amount: Number(x.amount),
          status: x.status, method: x.status === "paid" ? x.method : null,
        })),
      });
      toast.success(`Added ${r.data.inserted} external trip(s) to payroll`);
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <div data-testid="external-trips-modal" className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#050505] border border-white/10 max-w-5xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-1">
          <h3 className="font-serif text-xl">Add external platform trips</h3>
          <button data-testid="external-trips-close" onClick={onClose} className="text-[#71717A] hover:text-[#F5F2EB] text-xl leading-none">×</button>
        </div>
        <p className="text-[11px] text-[#A1A1AA] mb-4">
          Trips your drivers ran on Luxy, GoJo, BlackCarEverywhere, B2B affiliates, etc. Enter the <b className="text-[#F5F2EB]">driver pay</b> per trip — they merge into the payroll sheet and 1099 totals.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-[11px] mb-3">
            <thead className="text-[9px] uppercase tracking-[0.18em] text-[#71717A]">
              <tr>
                <th className="text-left pr-2 pb-1">Driver</th>
                <th className="text-left pr-2">Date</th>
                <th className="text-left pr-2">Platform</th>
                <th className="text-left pr-2">Description / route</th>
                <th className="text-left pr-2">Driver pay $</th>
                <th className="text-left pr-2">Status</th>
                <th className="text-left pr-2">Method</th>
                <th/>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} data-testid={`ext-row-${i}`}>
                  <td className="pr-2 py-1">
                    <select data-testid={`ext-driver-${i}`} value={r.driver_id} onChange={e => set(i, "driver_id", e.target.value)}
                      className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-[#F5F2EB] w-40">
                      <option value="">Select…</option>
                      {drivers.map(d => <option key={d.driver_id} value={d.driver_id}>{d.name}</option>)}
                    </select>
                  </td>
                  <td className="pr-2"><input type="date" data-testid={`ext-date-${i}`} value={r.date} onChange={e => set(i, "date", e.target.value)}
                    className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-[#F5F2EB] w-36"/></td>
                  <td className="pr-2">
                    <input list="ext-platforms" data-testid={`ext-platform-${i}`} value={r.platform} onChange={e => set(i, "platform", e.target.value)}
                      className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-[#F5F2EB] w-32"/>
                  </td>
                  <td className="pr-2"><input data-testid={`ext-desc-${i}`} value={r.description} onChange={e => set(i, "description", e.target.value)}
                    placeholder="MKE → ORD" className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-[#F5F2EB] w-full min-w-[140px]"/></td>
                  <td className="pr-2"><input type="number" min="0" step="0.01" data-testid={`ext-amount-${i}`} value={r.amount} onChange={e => set(i, "amount", e.target.value)}
                    placeholder="0.00" className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-[#F5F2EB] w-24 text-right font-mono"/></td>
                  <td className="pr-2">
                    <select data-testid={`ext-status-${i}`} value={r.status} onChange={e => set(i, "status", e.target.value)}
                      className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-[#F5F2EB]">
                      <option value="paid">paid</option><option value="unpaid">unpaid</option>
                    </select>
                  </td>
                  <td className="pr-2">
                    <select data-testid={`ext-method-${i}`} value={r.method} disabled={r.status !== "paid"} onChange={e => set(i, "method", e.target.value)}
                      className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-[#F5F2EB] disabled:opacity-30">
                      {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </td>
                  <td>{rows.length > 1 && (
                    <button data-testid={`ext-del-${i}`} onClick={() => delRow(i)} className="text-[#71717A] hover:text-red-300 p-1"><Trash2 size={13}/></button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <datalist id="ext-platforms">{PLATFORMS.map(p => <option key={p} value={p}/>)}</datalist>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button data-testid="ext-add-row" onClick={addRow} className="btn-ghost !py-1.5 !px-3 text-[10px]"><Plus size={12}/>Add row</button>
          <button data-testid="ext-import-csv" onClick={() => fileRef.current?.click()} className="btn-ghost !py-1.5 !px-3 text-[10px]">
            <Upload size={12}/>Import CSV
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
            onChange={e => { if (e.target.files?.[0]) importCsv(e.target.files[0]); e.target.value = ""; }}/>
          <span className="text-[10px] text-[#71717A]">CSV columns: Driver name, Date (YYYY-MM-DD), Platform, Description, Amount, Status, Method</span>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-[11px] text-[#A1A1AA]">Total: <span className="text-[#D4AF37] font-serif text-base">${total.toFixed(2)}</span></span>
            <button data-testid="ext-save" onClick={save} disabled={busy} className="btn-gold !py-2 !px-5">{busy ? "Saving…" : `Save ${rows.filter(r => r.driver_id && Number(r.amount) > 0).length} trip(s)`}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
